import moment from "moment-timezone";
import { normalize, denormalize } from "normalizr";
import { createSelector } from "reselect";

import type {
    IIssue,
    ISiteSlim,
    SiteMini,
    SiteStatistics,
    IResponseWithLoading,
    IResponseWithLoadingAndTimestamp,
} from "App/Modules/Models";
import { IssueSeverity, SiteState } from "App/Modules/Models";
import type { CombinedSite, SiteIssuesInfo } from "../Models";
import { mergeResponsesWithLoading } from "../Shared";
import { siteSchema, issueSchema } from "./modelSchemas";

const sitesSelector = (state: { sites: IResponseWithLoading<ISiteSlim[]> }) => state.sites;
const sitesStatisticsSelector = (state: { sitesStatistics: IResponseWithLoading<SiteStatistics[]> }) =>
    state.sitesStatistics;
const issuesSelector = (state: { issues: IResponseWithLoading<IIssue[]> }) => state.issues;

const normalizeSitesSelector = createSelector(sitesSelector, (sites) => {
    const { data, ...loadingProps } = sites;
    if (data) {
        const normalizedSites = normalize(data, [siteSchema]);
        return {
            data: normalizedSites,
            ...loadingProps,
        } as IResponseWithLoading<typeof normalizedSites>;
    }
    return { ...loadingProps };
});

const normalizeSitesStatisticsSelector = createSelector(sitesStatisticsSelector, (sites) => {
    const { data, ...loadingProps } = sites;
    if (data) {
        const normalizedSites = normalize(data, [siteSchema]);
        return {
            data: normalizedSites,
            ...loadingProps,
        } as IResponseWithLoading<typeof normalizedSites>;
    }
    return { ...loadingProps };
});

const normalizeIssuesSelector = createSelector(issuesSelector, (issues) => {
    const { data, ...loadingProps } = issues;
    if (data) {
        const normalizedIssues = normalize(data, [issueSchema]);
        return {
            data: normalizedIssues,
            ...loadingProps,
        } as IResponseWithLoading<typeof normalizedIssues>;
    }
    return { ...loadingProps };
});

const issuesCountPerSiteSelector = createSelector(normalizeIssuesSelector, (issues) => {
    const { data, ...loadingProps } = issues;
    if (data && data.entities?.issues) {
        const sites: { [key: string]: SiteIssuesInfo } = {};

        Object.values(data.entities.issues).forEach((i: IIssue) => {
            i.relatedSites?.forEach((site: SiteMini) => {
                if (!sites[site.id]) {
                    sites[site.id] = {
                        dtLatestIssue: i.dtIssue,
                        issues: [],
                        numIssuesError: 0,
                        numIssuesOther: 0,
                        numIssuesWarning: 0,
                        numIssuesActive: 0,
                        numIssuesNew: 0,
                    };
                }
                sites[site.id].dtLatestIssue = moment.max(sites[site.id].dtLatestIssue, i.dtIssue);
                sites[site.id].issues.push(i);

                if (i.isActive) {
                    switch (i.severity) {
                        case IssueSeverity.Error:
                            sites[site.id].numIssuesError++;
                            break;
                        case IssueSeverity.Warning:
                            sites[site.id].numIssuesWarning++;
                            break;
                        case IssueSeverity.Other:
                            sites[site.id].numIssuesOther++;
                            break;
                        default:
                            throw new Error(`Issue severity not supported ${i.severity as any}`);
                    }
                    sites[site.id].numIssuesActive++;
                }
                if (!i.isAcknowledged) {
                    sites[site.id].numIssuesNew++;
                }
            });
        });
        return { data: sites, ...loadingProps } as IResponseWithLoading<typeof sites>;
    }
    return { ...loadingProps };
});

export const combinedSitesSelector = createSelector(
    normalizeSitesSelector,
    normalizeSitesStatisticsSelector,
    issuesCountPerSiteSelector,
    (nSites, nSitesStatistics, nIssues) => {
        const { data: nSitesData, ...sitesLoadingProps } = nSites;
        const { data: nSitesStatisticsData, ...sitesStatisticsLoadingProps } = nSitesStatistics;
        const { data: nIssuesData, ...issuesLoadingProps } = nIssues;

        const loadingProps = mergeResponsesWithLoading(
            sitesLoadingProps,
            sitesStatisticsLoadingProps,
            issuesLoadingProps,
        );

        if (!nSitesData) {
            return { data: [] as CombinedSite[], ...loadingProps };
        }

        let sitesCombined: ISiteSlim[] = denormalize(nSitesData.result, [siteSchema], nSitesData.entities);
        if (nSitesStatisticsData && nSitesStatisticsData.entities.sites) {
            sitesCombined = sitesCombined.map((s) => ({
                ...s,
                ...nSitesStatisticsData.entities.sites[s.id],
            }));
        }
        if (nIssuesData) {
            sitesCombined = sitesCombined.map((s) => ({
                ...s,
                ...nIssuesData[s.id],
            }));
        }

        sitesCombined = sitesCombined.map((s) => ({
            ...s,
            siteStateIsEnded: s.siteState === SiteState.Ended,
        }));

        return {
            ...loadingProps,
            data: sitesCombined,
        } as IResponseWithLoadingAndTimestamp<CombinedSite[]>;
    },
);
